<template>
  <div>
    <div
      :class="[
        'flex flex-col gap-2 rounded-lg',
        { 'border border-red-500 bg-red-50 p-2': validation }
      ]"
    >
      <div class="font-medium">
        <Tooltip :options="{ theme: 'light' }" placement="bottom">
          <template #trigger>
            {{ dateLabel }}
            <i class="fa-regular fa-chevron-down" />
          </template>
          <template #tooltip>
            <Calendar
              v-model="selectedDate"
              class="flex-1 flex-shrink"
              :min-date="new Date()"
            ></Calendar>
          </template>
        </Tooltip>
      </div>
      <div class="flex flex-row overflow-x-auto w-full gap-3">
        <div
          v-for="day in days"
          :key="day.name + day.number"
          :class="[
            'flex flex-col gap-1 items-center justify-center h-16 w-16 flex-shrink-0 border rounded-lg ',
            {
              'bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 cursor-pointer selected-day':
                isSelected(day)
            },
            {
              'bg-white hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 cursor-pointer':
                !isSelected(day) && !day.disabled
            },
            {
              'bg-gray-100 border-gray-300 text-gray-500 disabled': day.disabled
            }
          ]"
          @click="selectDay(day)"
        >
          <div>{{ day.name }}</div>
          <div>{{ day.number }}</div>
        </div>
      </div>
      <div class="font-medium">Appointment Time</div>
      <div
        class="grid grid-cols-2 md:grid-cols-4 gap-2 overflow-scroll max-h-36"
      >
        <button
          v-for="timeSlot in timeSlots"
          :key="timeSlot.scheduled_time"
          type="button"
          class="py-2 px-3 text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-200 transition-colors duration-200 rounded-lg border whitespace-nowrap"
          :class="{
            // Status 1 styling: Gray (DISABLED - HIGHEST PRIORITY)
            'bg-gray-100 text-gray-300 disabled': isPast(
              timeSlot.scheduled_time
            ),
            // Selected styling (OVERRIDE) - SECOND HIGHEST PRIORITY
            'bg-yellow-300 text-white cursor-pointer':
              selectedTimeSlot?.scheduled_time === timeSlot.scheduled_time &&
              timeSlot.disabled,

            // Selected styling (NORMAL) - THIRD PRIORITY
            'bg-green-400 text-white cursor-pointer':
              selectedTimeSlot?.scheduled_time === timeSlot.scheduled_time &&
              !timeSlot.disabled,

            // Status 2 styling: Yellow/Amber (OVERRIDE - NOT SELECTED) - FOURTH PRIORITY
            'bg-yellow-100 hover:bg-yellow-300 text-yellow-500 hover:text-white cursor-pointer':
              !isPast(timeSlot.scheduled_time) &&
              timeSlot.disabled &&
              selectedTimeSlot?.scheduled_time !== timeSlot.scheduled_time,

            // Default styling (available) - LOWEST PRIORITY
            'text-green-600 bg-green-200 hover:bg-green-400 hover:text-white cursor-pointer':
              !timeSlot.disabled &&
              selectedTimeSlot?.scheduled_time !== timeSlot.scheduled_time
          }"
          :disabled="isPast(timeSlot.scheduled_time)"
          @click="selectTimeSlot(timeSlot)"
        >
          {{ getTimeSlotLabel(timeSlot) }}
        </button>
      </div>
    </div>
    <!-- Helper Text -->
    <p v-if="validation" class="text-red-700 mt-2">
      <span class="font-medium">Oops!</span>
      {{ validation }}
    </p>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import Calendar from "@/components/Calendar.vue";
import { useAppointmentAvailability } from "@/AppointmentCreateModal/useAppointmentAvailability";

const emit = defineEmits(["update:modelValue"]);
const {
  timeSlots,
  getTimeSlotLabel,
  selectTimeSlot,
  selectedTimeSlot,
  isPast,
  getMonthAvailability,
  getDateTimeSlots,
  clearCache
} = useAppointmentAvailability();
const props = defineProps<{
  serviceCategoryId: number;
  durationMinutes?: number;
  zipcode: string;
  userId?: number;
  userIds?: number[];
  validation?: string;
  scheduled_time?: string;
  arrival_window_end?: string;
  arrival_window_start?: string;
  unavailable_time?: boolean;
}>();

interface Day {
  name: string;
  number: string;
  disabled: boolean;
  date: Date;
}

const selectedDate = ref(new Date());
const days = ref<Day[]>([]);

const requestParams = computed(() => {
  return {
    serviceCategoryId: props.serviceCategoryId,
    durationMinutes: props.durationMinutes,
    userId: props.userId,
    userIds: props.userIds,
    zipcode: props.zipcode
  };
});

onMounted(async () => {
  // Set initial date from scheduled_time prop
  if (props.scheduled_time) {
    const parsedMoment = moment(props.scheduled_time);
    selectedDate.value = parsedMoment.toDate();
  }
  await fetchAvailability();

  // After fetching time slots, select the one matching the target hour in the tenant's timezone
  // Match based on scheduled_time to preserve original behavior
  if (props.scheduled_time && timeSlots.value.length > 0) {
    // Use parseZone to preserve the timezone from the string (tenant timezone)
    const targetMoment = moment.parseZone(props.scheduled_time);
    const targetHour = targetMoment.hour();
    const now = moment();

    // First, try to find a slot whose scheduled_time matches the target hour
    let matchingSlot = timeSlots.value.find(
      (slot) => moment.parseZone(slot.scheduled_time).hour() === targetHour
    );

    // If no exact match, find the closest slot by scheduled_time hour
    if (!matchingSlot) {
      let closestSlot = timeSlots.value[0];
      let smallestDiff = Math.abs(
        moment.parseZone(closestSlot.scheduled_time).hour() - targetHour
      );

      for (const slot of timeSlots.value) {
        const slotHour = moment.parseZone(slot.scheduled_time).hour();
        const diff = Math.abs(slotHour - targetHour);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestSlot = slot;
        }
      }
      matchingSlot = closestSlot;
    }

    // If the matched slot is in the past, find the first future slot at or after the target hour
    if (moment(matchingSlot.scheduled_time).isBefore(now)) {
      const futureSlots = timeSlots.value.filter(
        (slot) => !moment(slot.scheduled_time).isBefore(now)
      );

      if (futureSlots.length > 0) {
        // Prefer a future slot at or after the target hour
        const slotsAtOrAfterTarget = futureSlots.filter(
          (slot) => moment.parseZone(slot.scheduled_time).hour() >= targetHour
        );
        matchingSlot =
          slotsAtOrAfterTarget.length > 0
            ? slotsAtOrAfterTarget[0]
            : futureSlots[0];
      }
    }

    selectTimeSlot(matchingSlot);
  }
});

const fetchAvailability = async () => {
  await getMonthAvailability({
    month: selectedDate.value,
    ...requestParams.value
  });
  await getDateTimeSlots({
    date: selectedDate.value,
    ...requestParams.value
  });
  setMonthDays();
};

const setMonthDays = () => {
  const list: Day[] = [];
  const from = moment(selectedDate.value).startOf("month");
  const to = moment(selectedDate.value).endOf("month");
  for (let m = moment(from); m.isSameOrBefore(to); m.add(1, "days")) {
    list.push({
      name: m.format("dd"),
      number: m.format("D"),
      disabled: !m.isSameOrAfter(moment().startOf("day")),
      date: m.toDate()
    });
  }
  days.value = list;
  nextTick(() => {
    const selectedDayEl = $(".selected-day")[0];
    if (selectedDayEl) {
      if (selectedDayEl.scrollIntoViewIfNeeded) {
        selectedDayEl.scrollIntoViewIfNeeded();
      } else {
        selectedDayEl.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }
  });
};

const dateLabel = computed(() =>
  moment(selectedDate.value).format("dddd: MMM DD, YYYY")
);

const isSelected = (day: Day) => {
  const selected = moment(selectedDate.value).isSame(moment(day.date), "day");
  return selected;
};

const selectDay = (day: Day) => {
  selectedDate.value = day.date;
};

watch(selectedDate, (newVal, oldVal) => {
  if (!moment(newVal).isSame(moment(oldVal), "day")) {
    setMonthDays();
    getDateTimeSlots({
      ...requestParams.value,
      date: selectedDate.value
    });
  }
});

watch(selectedTimeSlot, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emit("update:modelValue", newVal);
  }
});

watch(
  requestParams,
  (newVal, oldVal) => {
    if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
      clearCache();
      fetchAvailability();
    }
  },
  { deep: true }
);
</script>

<style scoped></style>
